import { Injectable } from '@angular/core';
import { UserManager, User, UserManagerSettings } from 'oidc-client';
//import { Constants } from "../constants";
import {
    BehaviorSubject,
    from,
    map,
    Observable,
    Observer,
    Subject,
} from 'rxjs';
import { Constants } from './constants';
import { RegisterRedirectUrlModel } from '../models/register-redirect-url.model';
import { HttpClient } from '@angular/common/http';
import { RegisterModel } from '../models/register.model';
import { Router } from '@angular/router';
import { BusyService } from '../pathways-ui/services/busy.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private _userManager: UserManager;
    private _user: User;
    private currentUserSource = new BehaviorSubject<User | null>(null);
    currentUser$ = this.currentUserSource.asObservable();
    private _loginChangedSubject = new BehaviorSubject<boolean>(false);

    public loginChanged = this._loginChangedSubject.asObservable();

    authController = '/api/auth';
    private get idpSettings(): UserManagerSettings {
        return {
            authority: Constants.idpAuthority,
            client_id: Constants.clientId,
            redirect_uri: `${Constants.clientRoot}/signin-callback`,
            scope: `openid profile ${Constants.clientId}_api`,
            response_type: 'code',
            post_logout_redirect_uri: `${Constants.clientRoot}/signout-callback`,
            silent_redirect_uri: `${Constants.clientRoot}/silent-refresh-callback`,
            // silent_redirect_uri: `${Constants.clientRoot}/assets/silent-refresh.html`,
            automaticSilentRenew: true,
        };
    }

    constructor(private _busyService: BusyService, private _http: HttpClient, private _router: Router) {
        this._userManager = new UserManager(this.idpSettings);
        this.isAuthenticated().then();
        this._userManager.events.addAccessTokenExpired((_) => {
            console.log('token expired');
            this._user = null;
            this.currentUserSource.next(null);
            this._loginChangedSubject.next(false);
            this._router.navigate(['/']);
        });
    }

    getRegisterLink(
        registerObj: RegisterModel
    ): Observable<RegisterRedirectUrlModel> {
        return this._http
            .post<RegisterRedirectUrlModel>(
                `${this.authController}/register`,
                registerObj
            )
            .pipe(map((data) => data));
    }

    isLoggedInObs(): Observable<boolean> {
        return from(this._userManager.getUser()).pipe(
            map((user) => {
                if (user && !user.expired) {
                    return true;
                } else {
                    return false;
                }
            })
        );
        // .map<User, boolean>((user) => {
        //   if (user) {
        //     return true;
        //   } else {
        //     return false;
        //   }
        // });
    }
    public login = () => {
        this._busyService.busy(false);
        const currentLanguage = localStorage.getItem('language') || 'en';

        return this._userManager.signinRedirect({
            extraQueryParams: {
                ui_locale: currentLanguage,
            },
        }).then(_ => {
            this._busyService.idle(1500);

        });

    };
    public getUserProfile() {
        return this._user?.profile;
    }
    public isAuthenticated = (): Promise<boolean> => {
        return this._userManager.getUser().then((user) => {
            if (this._user !== user) {
                this._loginChangedSubject.next(this.checkUser(user));
            }

            this._user = user;

            return this.checkUser(user);
        });
    };
    public checkUserApp() {
        if (this.checkUser(this._user)) {
            this.currentUserSource.next(this._user);
        }
    }
    public finishLogin = (): Promise<User> => {
        return this._userManager.signinRedirectCallback().then((user) => {
            this._user = user;
            this._loginChangedSubject.next(this.checkUser(user));
            this.currentUserSource.next(user);
            return user;
        });
    };
    public finishSilentRefresh = () => {
        return this._userManager
            .signinSilentCallback()
            .then((u) => {
                // removed this line to prevent the user from being set to null
            })
            .catch((err) => {
                console.log(err);
            });
    };
    public logout = () => {
        const currentLanguage = localStorage.getItem('language') || 'en';
        return this._userManager.signoutRedirect({
            extraQueryParams: {
                ui_locale: currentLanguage,
            },
        });
    };

    public revoke = async () => {
        this._user = null;
        await this._userManager.removeUser();
        this.currentUserSource.next(this._user);
        // this._userManager.signoutRedirectCallback();
    };

    public finishLogout = () => {
        this._user = null;
        this._loginChangedSubject.next(false);
        this.currentUserSource.next(this._user);
        return this._userManager.signoutRedirectCallback();
    };

    public getAccessToken = (): Promise<string> => {
        return this._userManager.getUser().then((user) => {
            return !!user && !user.expired ? user.access_token : null;
        });
    };
    public getProfile = () => {
        return this._userManager.getUser().then((user) => {
            return !!user && !user.expired ? user.profile : null;
        });
    };
    public isLoggedInAndMemberOfPathwaysAdmin = () => {
        return this._userManager.getUser().then((user) => {
            if (!user || user.expired) {
                return false;
            }
            if (
                user.profile['org'] !== 'PL000001' ||
                !user.profile['isAdmin']
            ) {
                return false;
            }
            return true;
        });
    };
    public isAuthenticatedAndHasOrg = () => {
        return this._userManager.getUser().then((user) => {
            if (!user || user.expired) {
                return false;
            }
            if (!user.profile['org']) {
                return false;
            }
            return true;
        });
    };
    private checkUser = (user: User): boolean => {
        return !!user && !user.expired;
    };
}
